import { useState } from 'react';
import { Card, Button, Loading } from '../common';
import { useAuthStore } from '../../store/authStore';
import type { TarotReading } from '../../types/tarot';

interface SharingOptionsProps {
  reading?: TarotReading;
  onShare?: (shareData: ShareData) => void;
}

interface ShareData {
  readingId: string;
  shareType: 'link' | 'image' | 'text';
  includeInterpretation: boolean;
  includeCards: boolean;
  includeQuestion: boolean;
  isPublic: boolean;
  expiresAt?: string;
}

export function SharingOptions({ reading, onShare }: SharingOptionsProps) {
  const { user } = useAuthStore();
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  
  const [shareOptions, setShareOptions] = useState({
    shareType: 'link' as 'link' | 'image' | 'text',
    includeInterpretation: true,
    includeCards: true,
    includeQuestion: false, // Default to false for privacy
    isPublic: false, // Default to private sharing
    expiresIn: '7d' as '1h' | '24h' | '7d' | '30d' | 'never'
  });

  const handleOptionChange = (option: string, value: boolean | string) => {
    setShareOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const getExpirationDate = () => {
    if (shareOptions.expiresIn === 'never') return undefined;
    
    const now = new Date();
    switch (shareOptions.expiresIn) {
      case '1h':
        return new Date(now.getTime() + 60 * 60 * 1000);
      case '24h':
        return new Date(now.getTime() + 24 * 60 * 60 * 1000);
      case '7d':
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      case '30d':
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      default:
        return undefined;
    }
  };

  const checkPrivacySettings = () => {
    // Check if user allows reading sharing
    const allowsSharing = user?.preferences?.privacy?.allowReadingSharing !== false;
    
    if (!allowsSharing) {
      setError('Reading sharing is disabled in your privacy settings. Please enable it to share readings.');
      return false;
    }

    // Check if reading is private and warn user
    if (reading?.isPrivate && shareOptions.isPublic) {
      setError('This reading is marked as private. Making it publicly shareable will override the private setting.');
      return false;
    }

    return true;
  };

  const generateShareableContent = () => {
    if (!reading) return '';

    let content = '';
    
    if (shareOptions.includeQuestion && reading.question) {
      content += `Question: ${reading.question}\n\n`;
    }

    if (shareOptions.includeCards) {
      content += 'Cards:\n';
      reading.cards.forEach((card, index) => {
        content += `${index + 1}. ${card.name}${card.isReversed ? ' (Reversed)' : ''}\n`;
      });
      content += '\n';
    }

    if (shareOptions.includeInterpretation) {
      if (reading.aiInterpretation) {
        content += `Interpretation:\n${reading.aiInterpretation}\n\n`;
      } else if (reading.interpretation) {
        content += `Interpretation:\n${reading.interpretation}\n\n`;
      }
    }

    content += 'Shared from Tarot Card Reader App';
    return content;
  };

  const handleShare = async () => {
    if (!reading) {
      setError('No reading selected for sharing');
      return;
    }

    if (!checkPrivacySettings()) {
      return;
    }

    try {
      setIsSharing(true);
      setError(null);
      setSuccess(null);

      const shareData: ShareData = {
        readingId: reading.id,
        shareType: shareOptions.shareType,
        includeInterpretation: shareOptions.includeInterpretation,
        includeCards: shareOptions.includeCards,
        includeQuestion: shareOptions.includeQuestion,
        isPublic: shareOptions.isPublic,
        expiresAt: getExpirationDate()?.toISOString()
      };

      if (shareOptions.shareType === 'link') {
        // Generate shareable link (this would typically involve creating a share token on the server)
        const shareToken = `${reading.id}-${Date.now()}`;
        const baseUrl = window.location.origin;
        const generatedUrl = `${baseUrl}/shared/${shareToken}`;
        
        setShareUrl(generatedUrl);
        
        // Copy to clipboard
        await navigator.clipboard.writeText(generatedUrl);
        setSuccess('Share link copied to clipboard!');
        
      } else if (shareOptions.shareType === 'text') {
        // Generate text content and copy to clipboard
        const textContent = generateShareableContent();
        await navigator.clipboard.writeText(textContent);
        setSuccess('Reading content copied to clipboard!');
        
      } else if (shareOptions.shareType === 'image') {
        // This would generate an image representation of the reading
        setSuccess('Image sharing feature coming soon!');
      }

      // Call the onShare callback if provided
      onShare?.(shareData);

    } catch (error: any) {
      console.error('Failed to share reading:', error);
      setError('Failed to create share link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyUrl = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setSuccess('Share link copied to clipboard!');
      } catch (error) {
        setError('Failed to copy link to clipboard');
      }
    }
  };

  // Check if user has sharing enabled
  const sharingEnabled = user?.preferences?.privacy?.allowReadingSharing !== false;

  if (!sharingEnabled) {
    return (
      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Sharing Options</h3>
        <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-400 text-sm">
            Reading sharing is currently disabled in your privacy settings. 
            Enable it in Privacy Settings to share your readings.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Share Reading</h3>
        
        {reading?.isPrivate && (
          <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg mb-6">
            <p className="text-yellow-400 text-sm">
              ⚠️ This reading is marked as private. Sharing will make it accessible to others.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {/* Share Type */}
          <div>
            <h4 className="text-lg font-medium text-white mb-3">Share Format</h4>
            <div className="space-y-2">
              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="shareType"
                  value="link"
                  checked={shareOptions.shareType === 'link'}
                  onChange={(e) => handleOptionChange('shareType', e.target.value)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-white">Shareable Link</span>
                  <p className="text-gray-400 text-sm">Create a link that others can view</p>
                </div>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="shareType"
                  value="text"
                  checked={shareOptions.shareType === 'text'}
                  onChange={(e) => handleOptionChange('shareType', e.target.value)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-white">Text Content</span>
                  <p className="text-gray-400 text-sm">Copy reading as formatted text</p>
                </div>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="shareType"
                  value="image"
                  checked={shareOptions.shareType === 'image'}
                  onChange={(e) => handleOptionChange('shareType', e.target.value)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 focus:ring-primary-500"
                  disabled
                />
                <div>
                  <span className="text-gray-500">Image (Coming Soon)</span>
                  <p className="text-gray-400 text-sm">Generate a visual representation</p>
                </div>
              </label>
            </div>
          </div>

          {/* Content Options */}
          <div>
            <h4 className="text-lg font-medium text-white mb-3">What to Include</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={shareOptions.includeCards}
                  onChange={(e) => handleOptionChange('includeCards', e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-white">Card Names and Positions</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={shareOptions.includeInterpretation}
                  onChange={(e) => handleOptionChange('includeInterpretation', e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-white">Interpretation</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={shareOptions.includeQuestion}
                  onChange={(e) => handleOptionChange('includeQuestion', e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-white">Question</span>
                  <p className="text-gray-400 text-sm">⚠️ May contain personal information</p>
                </div>
              </label>
            </div>
          </div>

          {/* Privacy Options */}
          {shareOptions.shareType === 'link' && (
            <div>
              <h4 className="text-lg font-medium text-white mb-3">Privacy & Access</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={shareOptions.isPublic}
                    onChange={(e) => handleOptionChange('isPublic', e.target.checked)}
                    className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-white">Make Public</span>
                    <p className="text-gray-400 text-sm">Allow search engines to index this reading</p>
                  </div>
                </label>

                <div>
                  <label className="text-white font-medium mb-2 block">Link Expires</label>
                  <select
                    value={shareOptions.expiresIn}
                    onChange={(e) => handleOptionChange('expiresIn', e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="1h">1 Hour</option>
                    <option value="24h">24 Hours</option>
                    <option value="7d">7 Days</option>
                    <option value="30d">30 Days</option>
                    <option value="never">Never</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">
                Shared content respects your privacy settings.
              </p>
              {shareUrl && (
                <div className="mt-2">
                  <p className="text-gray-400 text-sm mb-1">Share URL:</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-gray-800 px-2 py-1 rounded text-sm text-gray-300 flex-1 truncate">
                      {shareUrl}
                    </code>
                    <Button variant="ghost" size="sm" onClick={handleCopyUrl}>
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="primary"
              onClick={handleShare}
              disabled={isSharing || !reading}
            >
              {isSharing ? <Loading size="sm" /> : 'Share Reading'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}